import { NotificationType } from '@electricjs/core_entity-client';
import { Flex } from '@electricjs/arc';
import { Trans } from 'react-i18next';

import i18n from '@/i18n';
import { useGetXboardingData } from '@/hooks/useGetXboardingData';
import { EmployeeDirectoryListing } from '@/types/employees';
import { Notification } from '@/types/notifications';

import { EmailDetail } from '../EmailDetail';
import { GroupDetail } from '../GroupDetail';
import { NotificationIcon } from '../NotificationIcon';
import { NotificationItemHeader } from '../NotificationItemHeader';
import { NotificationItemProps } from '../types';
import { RequestCompleted } from '../RequestActions/RequestCompleted';
import { getDisplayFullName } from 'common/utils/getFullName';

const getTitle = (
  notification: Notification,
  employee?: EmployeeDirectoryListing
) => {
  const employeeName = employee
    ? getDisplayFullName(employee)
    : i18n.t('common:notificationItems.anEmployee');

  switch (notification.type) {
    case NotificationType.OnboardingCompleted:
      return (
        <Trans
          i18nKey="notificationItems.xboardingComplete.onboardingComplete"
          ns="common"
          values={{ employeeName }}
          components={{ bold: <b /> }}
        />
      );
    case NotificationType.OffboardingCompleted:
      return (
        <Trans
          i18nKey="notificationItems.xboardingComplete.offboardingComplete"
          ns="common"
          values={{ employeeName }}
          components={{ bold: <b /> }}
        />
      );
    default:
      return <>{i18n.t('common:notificationItems.defaultMessage')}</>;
  }
};

export const XboardingCompleteAction = () => {
  return (
    <Flex m="2">
      <RequestCompleted />
    </Flex>
  );
};

export const XboardingDetailsColumn = ({
  notification,
}: NotificationItemProps) => {
  const { data: xboardingData, isXboardingLoading } = useGetXboardingData({
    employeeId: notification?.data?.employee_id ?? null,
  });
  const employeeGroups = xboardingData.employeeGroups;

  const employeeEmail = xboardingData?.employee?.email;

  return (
    <Flex flexDirection="column">
      <GroupDetail isLoading={isXboardingLoading} groups={employeeGroups} />
      <EmailDetail isLoading={isXboardingLoading} email={employeeEmail} />
    </Flex>
  );
};

const XboardingCompleteNotificationItem = ({
  notification,
  hideDetails,
}: NotificationItemProps) => {
  const { data: requestData, isXboardingLoading: isLoading } =
    useGetXboardingData({
      employeeId: notification?.data?.employee_id ?? null,
    });

  const getLabel = () => {
    switch (notification.type) {
      case NotificationType.OnboardingCompleted:
        return i18n.t('common:notificationItems.labels.onboardingCompleted');

      case NotificationType.OffboardingCompleted:
        return i18n.t('common:notificationItems.labels.offboardingCompleted');
      default:
        return '';
    }
  };

  const employeeGroups = requestData.employeeGroups ?? undefined;

  const employeeEmail = requestData?.employee?.email ?? undefined;

  if (hideDetails) {
    return (
      <NotificationItemHeader
        notification={notification}
        isLoading={isLoading}
        title={getTitle(notification, requestData.employee)}
        icon={
          <NotificationIcon
            isNotificationUnseen={!notification.seenAt}
            loading={isLoading}
            icon="user"
          />
        }
        label={getLabel()}
      />
    );
  }

  return (
    <NotificationItemHeader
      notification={notification}
      isLoading={isLoading}
      title={getTitle(notification, requestData.employee)}
      icon={
        <NotificationIcon
          isNotificationUnseen={!notification.seenAt}
          loading={isLoading}
          icon="user"
        />
      }
      label={getLabel()}>
      {!hideDetails ? (
        <>
          <GroupDetail isLoading={isLoading} groups={employeeGroups} />
          <EmailDetail isLoading={isLoading} email={employeeEmail} />
          <XboardingCompleteAction />
        </>
      ) : null}
    </NotificationItemHeader>
  );
};

export default XboardingCompleteNotificationItem;
